import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../../lib/api";
import BetaHmIndicator from "../../../components/BetaHmIndicator";
import AllergyBadge from "../../../components/AllergyBadge";
import { useProjectAllergyMap } from "../../../lib/allergy";
import DeptBreakdownSection from "../../../components/DeptBreakdownSection";

/** Iter-158.3 — Beta H&M character list (read-only). */
export function BetaHMCharacters() {
    const { projectId } = useParams();
    const nav = useNavigate();
    const [chars, setChars] = useState([]);
    const [loading, setLoading] = useState(true);
    // Iter-158.13.2.20f.33 — allergy indicator for the Route 2
    // read-only Cast list. Uses the same `/supervisor-notes`
    // reader as PrepMode / SceneDetail — this component's own
    // read-only fetch happens against `/beta/hm/...` but notes
    // live on the main `/beta/hm-app/...` namespace. Pass the
    // canonical apiBase so both paths coexist.
    const { byCharacterId: allergyByCharacterId } = useProjectAllergyMap(
        `/beta/hm-app/projects`,
        projectId,
    );

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/beta/hm/projects/${projectId}/characters`);
            setChars(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(formatApiError(e));
            if (e?.response?.status === 404) nav("/beta");
        } finally {
            setLoading(false);
        }
    }, [projectId, nav]);

    useEffect(() => { refresh(); }, [refresh]);

    return (
        <div data-testid="beta-hm-characters" className="min-h-screen bg-neutral-950 text-neutral-100">
            <BetaHmIndicator />
            <div className="px-4 py-4 sm:px-8 sm:py-6">
                <header className="max-w-3xl mx-auto mb-6">
                    <Link
                        to={`/beta/projects/${projectId}/hm`}
                        className="tap text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-neutral-200"
                    >
                        ← Hair &amp; Makeup
                    </Link>
                    <h1 className="text-2xl sm:text-3xl font-semibold mt-2">Cast</h1>
                    <p className="text-[12px] text-neutral-500 mt-1">
                        {chars.length} {chars.length === 1 ? "character" : "characters"}
                    </p>
                </header>

                <div className="max-w-3xl mx-auto space-y-2">
                    {loading ? (
                        <p className="text-sm text-neutral-500">Loading…</p>
                    ) : chars.length === 0 ? (
                        <div className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-6 text-center">
                            <p className="text-sm text-neutral-300">No characters yet.</p>
                            <p className="text-[12px] text-neutral-500 mt-1">
                                Upload a script on the project screen to populate the cast.
                            </p>
                        </div>
                    ) : (
                        <ul className="space-y-2">
                            {chars.map((c) => (
                                <li key={c.id}>
                                    <Link
                                        data-testid={`beta-hm-character-row-${c.id}`}
                                        to={`/beta/projects/${projectId}/hm/characters/${c.id}`}
                                        className="tap block rounded-xl border border-neutral-800 bg-neutral-900/60 p-4 hover:bg-neutral-900"
                                    >
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-medium text-neutral-100 truncate flex-1">{c.name}</p>
                                            {/* Iter-158.13.2.20f.33 — indicator on the
                                                Route 2 read-only Cast list. Passive span
                                                variant (the row itself is a Link — no
                                                nested navigation). Users open the source
                                                allergy note via CharacterDetail on the
                                                canonical `/hm-app` cast surface. */}
                                            {allergyByCharacterId.get(c.id) ? (
                                                <span
                                                    data-testid={`beta-hm-character-row-allergy-${c.id}`}
                                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-red-400/40 bg-red-500/15 text-red-200 text-[9px] uppercase tracking-[0.15em] font-semibold shrink-0"
                                                >
                                                    <span aria-hidden="true">✚</span>
                                                    Allergy
                                                </span>
                                            ) : null}
                                        </div>
                                        <p className="text-[12px] text-neutral-500 mt-0.5">
                                            {c.scene_count} {c.scene_count === 1 ? "scene" : "scenes"}
                                        </p>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

/** Iter-158.3 — Beta H&M character detail (read-only). */
export function BetaHMCharacterDetail() {
    const { projectId, characterId } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/beta/hm/projects/${projectId}/characters/${characterId}`);
            setData(data);
        } catch (e) {
            toast.error(formatApiError(e));
            if (e?.response?.status === 404) nav(`/beta/projects/${projectId}/hm/characters`);
        } finally {
            setLoading(false);
        }
    }, [projectId, characterId, nav]);

    useEffect(() => { refresh(); }, [refresh]);

    if (loading) return <div className="min-h-screen bg-neutral-950 text-neutral-500 p-8 text-sm">Loading…</div>;
    if (!data) return null;

    return (
        <div data-testid="beta-hm-character-detail" className="min-h-screen bg-neutral-950 text-neutral-100">
            <BetaHmIndicator />
            <div className="px-4 py-4 sm:px-8 sm:py-6">
                <header className="max-w-3xl mx-auto mb-6">
                    <Link
                        to={`/beta/projects/${projectId}/hm/characters`}
                        className="tap text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-neutral-200"
                    >
                        ← Cast
                    </Link>
                    <h1
                        data-testid="beta-hm-character-name"
                        className="text-2xl sm:text-3xl font-semibold mt-2"
                    >
                        {data.character?.name}
                    </h1>
                    <p className="text-[12px] text-neutral-500 mt-1">
                        {data.scene_count} scene appearances
                    </p>
                </header>

                <div className="max-w-3xl mx-auto space-y-4">
                    <section className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-4">
                        <h2 className="text-[11px] uppercase tracking-[0.24em] text-neutral-400">Scenes</h2>
                        {data.scenes?.length ? (
                            <ul data-testid="beta-hm-character-scenes" className="mt-3 space-y-1">
                                {data.scenes.map((s) => (
                                    <li key={s.id} className="border-b border-neutral-800/60 py-1.5 last:border-b-0">
                                        <Link
                                            data-testid={`beta-hm-character-scene-row-${s.id}`}
                                            to={`/beta/projects/${projectId}/hm/scenes/${s.id}`}
                                            className="tap flex items-baseline gap-2 hover:text-white"
                                        >
                                            <span className="text-neutral-500 text-[11px] uppercase tracking-[0.2em] w-14 shrink-0">
                                                {s.number ? `#${s.number}` : `#${s.order}`}
                                            </span>
                                            <span className={`text-[13px] ${s.omitted ? "text-amber-300/70" : "text-neutral-200"}`}>
                                                {s.heading}
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="mt-2 text-sm text-neutral-500">This character doesn&rsquo;t appear in any scenes.</p>
                        )}
                    </section>

                    {/* Iter-158.13.2.20f.42a — Character-scoped H&M
                        Working Breakdown. Same canonical Route 1 mount
                        as the Scene view so the rows imported from
                        the immutable AI Breakdown (which resolve to
                        this Character via character_ids) show up here
                        too. Editing a row from either surface mutates
                        the same underlying document — text divergence
                        is impossible by construction. */}
                    <DeptBreakdownSection
                        apiBase="/projects"
                        projectId={projectId}
                        scope={{ character_id: characterId }}
                    />
                </div>
            </div>
        </div>
    );
}
